import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { PORTFOLIO_CATEGORIES } from "@/data/services";

export default function PortfolioGalleryPage() {
  const { slug } = useParams();
  const category = PORTFOLIO_CATEGORIES.find((c) => c.slug === slug);
  const [lightbox, setLightbox] = useState(null);

  if (!category) return <Navigate to="/portfolio" replace />;

  const photos = Array.from({ length: category.count }, (_, i) =>
    `/images/portfolio/${category.slug}/${String(i + 1).padStart(2, "0")}.jpg`
  );

  const navigateLightbox = (dir) => {
    if (lightbox === null) return;
    const next = dir === 1 ? (lightbox + 1) % photos.length : (lightbox - 1 + photos.length) % photos.length;
    setLightbox(next);
  };

  return (
    <div data-testid={`portfolio-gallery-${slug}`} className="min-h-screen">
      {/* Header */}
      <section className="pt-28 pb-8 sm:pt-36 sm:pb-12 md:pt-40 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-white/50 hover:text-gold text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Portfolio
          </Link>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold tracking-tighter uppercase text-white mb-4">
            {category.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="text-white/40 text-sm">
            {category.count} photos
          </motion.p>
        </div>
      </section>

      {/* Photo Grid */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {photos.map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                className="cursor-pointer group"
                onClick={() => setLightbox(i)}
              >
                <div className="relative rounded-xl overflow-hidden aspect-square">
                  <img
                    src={src}
                    alt={`${category.title} ${i + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white transition-all z-10">
              <X className="w-5 h-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }} className="absolute left-2 sm:left-6 w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }} className="absolute right-2 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all">
              <ChevronRight className="w-5 h-5" />
            </button>
            <motion.img
              key={lightbox}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              src={photos[lightbox]}
              alt={`${category.title} ${lightbox + 1}`}
              className="max-w-[85vw] max-h-[80vh] object-contain rounded-xl"
            />
            <div className="absolute bottom-6 text-center text-white/40 text-sm">
              {lightbox + 1} / {photos.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-16 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gold/5"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(255,191,0,0.4)] to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Ready to Make Your Business <span className="gold-shimmer">Shine?</span>
          </h2>
          <p className="text-white/50 text-sm sm:text-base mb-10 max-w-2xl mx-auto">
            Get a free consultation and discover how our custom signage solutions can transform your business visibility.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/booking" className="bg-gold text-black font-bold px-8 py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-1 flex items-center gap-2 text-sm sm:text-base">
              Book Free Consultation <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+17783220801" className="bg-transparent border border-white/20 text-white px-8 py-4 rounded-full hover:bg-white/5 hover:border-gold/50 transition-all flex items-center gap-2 text-sm sm:text-base">
              <Phone className="w-4 h-4" /> (778) 322-0801
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
